import React, { useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { MultiBackend, TouchTransition, MouseTransition } from "react-dnd-multi-backend";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { RotateCcw, ArrowLeft, Play, Volume2, HelpCircle, Menu, X } from "lucide-react";
import { QuestionPalette } from "./ui/QuestionPalette";
import { Word, Phoneme } from "../data/words";
import login_hero_image from '../assets/eko-_transparent-removebg-preview.png';
import TestBadge from './TestBadge';

/* ----------------------------- Drag & Drop ----------------------------- */
interface DropZoneProps {
  index: number;
  letter: string | null;
  onDrop: (letter: string, index: number) => void;
}

const DropZone: React.FC<DropZoneProps> = ({ index, letter, onDrop }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "letter",
    drop: (item: { letter: string }) => onDrop(item.letter, index),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-xl font-bold mx-1 select-none
        ${
          letter
            ? "bg-gradient-to-br from-green-300 to-green-400 border-2 border-green-500 text-green-800 shadow-md"
            : "bg-gradient-to-br from-yellow-200 to-yellow-300 border-2 border-yellow-400 shadow-inner"
        }
        ${isOver && canDrop ? "bg-gradient-to-br from-blue-300 to-blue-400 scale-110 shadow-lg ring-2 ring-blue-400 ring-opacity-50" : ""}
        transition-all duration-300 hover:shadow-lg touch-manipulation`}
      style={{ touchAction: 'none' }}
    >
      {letter ? (
        <span className="drop-shadow-sm">{letter.toUpperCase()}</span>
      ) : (
        <HelpCircle className="w-6 h-6 text-[#6B6FA3] animate-pulse" />
      )}
    </div>
  );
};

interface DraggableLetterProps {
  letter: string;
}

const DraggableLetter: React.FC<DraggableLetterProps> = ({ letter }) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: "letter",
    item: { letter },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <div
      ref={preview as unknown as React.Ref<HTMLDivElement>}
      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl letter-bounce
        bg-gradient-to-br from-purple-300 to-purple-400 border-2 border-purple-500 
        shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 select-none
        ${isDragging ? "opacity-60 scale-90 rotate-2" : ""}
        touch-manipulation`}
      style={{ touchAction: 'none' }}
    >
      <div
        ref={drag as unknown as React.Ref<HTMLDivElement>}
        className="w-full h-full flex items-center justify-center cursor-move touch-none
                   text-purple-800 drop-shadow-sm"
      >
        {letter.toUpperCase()}
      </div>
    </div>
  );
};

/* ----------------------------- Main Interface ----------------------------- */
type ResultRow = {
  word: string;
  user_input: string;
  type: string;
  time: number;
  hints_used: number;
};

interface UnifiedGameInterfaceProps {
  words: Word[];
  onComplete: (results: { words: ResultRow[] }) => void;
  onBackToDashboard: () => void;
  isDragMode: boolean;
  playerName: string;
  gradeName: string;
}

export const UnifiedGameInterface: React.FC<UnifiedGameInterfaceProps> = ({
  words,
  onComplete,
  onBackToDashboard,
  isDragMode,
  playerName,
  gradeName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [attemptedIndices, setAttemptedIndices] = useState<number[]>([]);
  const [skippedIndices, setSkippedIndices] = useState<number[]>([]);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [gradeNameState, setGradeName] = useState(gradeName);

  const currentWord = words[currentIndex];
  const totalWords = words.length;

  const phoneme = useMemo(
    () =>
      words[currentIndex].word.split("").map(
        (letter: string) =>
          ({
            sound: letter,
            letters: letter,
            type: /[aeiou]/i.test(letter) ? "vowel" : "consonant",
          } as Phoneme)
      ),
    [currentIndex, words]
  );

  const [userInput, setUserInput] = useState("");
  const typingInputRef = useRef<HTMLInputElement>(null);
  const [userPhonemes, setUserPhonemes] = useState<(string | null)[]>(
    new Array(phoneme?.length || 0).fill(null)
  );
  const [letterPool, setLetterPool] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [lastResult, setLastResult] = useState<'correct' | 'incorrect' | null>(null);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [currentWordTime, setCurrentWordTime] = useState<number>(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Allows Kindergarten users to switch from drag-and-drop to typing mode
  const [useTypingMode, setUseTypingMode] = useState(false);

  // Effective input mode: typing for non-Kindergarten grades, or Kindergarten
  // users who opted into typing via the toggle button.
  const isEffectiveTyping = gradeNameState !== 'Kindergarten' || useTypingMode;
  const isEffectiveDrag = gradeNameState === 'Kindergarten' && !useTypingMode;

  const progressPercentage = useMemo(
    () => ((currentIndex + 1) / totalWords) * 100,
    [currentIndex, totalWords]
  );

  // Motivational messages that rotate across the test
  const motivationalMessages = useMemo(() => [
    `Great start, {name}!`,
    `Awesome work, {name}!`,
    `Keep going, {name}!`,
    `You're doing great, {name}!`
  ], []);

  const firstName = useMemo(() => (playerName?.split(" ")[0] || playerName || ""), [playerName]);

  const currentMotivation = useMemo(() => {
    if (!totalWords || totalWords <= 0) return motivationalMessages[0].replace('{name}', firstName);
    const segments = motivationalMessages.length;
    const segmentSize = Math.max(1, Math.floor(totalWords / segments));
    const idx = Math.min(segments - 1, Math.floor(currentIndex / segmentSize));
    return motivationalMessages[idx].replace('{name}', firstName);
  }, [currentIndex, totalWords, motivationalMessages, firstName]);

  /* ----------------------------- Fix: Mobile / Tablet detection ----------------------------- */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------- Validation Functions ----------------------------- */
  // Check if typing mode input is complete
  const isTypingComplete = useMemo(() => {
    return userInput.trim().length > 0;
  }, [userInput]);

  // Check if drag mode is complete (all positions filled)
  const isDragComplete = useMemo(() => {
    return userPhonemes.every(letter => letter !== null);
  }, [userPhonemes]);

  // Overall completion check based on effective input mode
  const isAnswerComplete = useMemo(() => {
    if (isEffectiveTyping) {
      return isTypingComplete;
    } else {
      return isDragComplete;
    }
  }, [isEffectiveTyping, isDragComplete, isTypingComplete]);

  /* ----------------------------- Effects ----------------------------- */
  useEffect(() => {
    setIsComplete(false);
    setUserInput("");
    setUserPhonemes(new Array(phoneme.length).fill(null));
    // Reset timer for new word
    setStartTime(Date.now());
    setCurrentWordTime(0);

    if (isEffectiveDrag) {
      const correctLetters = phoneme.map((p) => p.letters);
      const alphabet = "abcdefghijklmnopqrstuvwxyz".split("");
      let pool = [...correctLetters];
      while (pool.length < 18) {
        const rand = alphabet[Math.floor(Math.random() * alphabet.length)];
        if (!pool.includes(rand)) pool.push(rand);
      }
      pool = pool.sort(() => Math.random() - 0.5);
      setLetterPool(pool);
    } else {
      setLetterPool([]);
    }
  }, [currentIndex, isEffectiveDrag, phoneme]);

  // Auto-focus the typing input when question changes (typing mode only)
  useEffect(() => {
    if (isEffectiveTyping && typingInputRef.current) {
      typingInputRef.current.focus();
    }
  }, [currentIndex, isEffectiveTyping]);

  // Timer effect to track current word time
  useEffect(() => {
    if (isComplete) return;

    const timer = setInterval(() => {
      setCurrentWordTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, isComplete]);

  /* ----------------------------- Handlers ----------------------------- */
  // Helper function to add unanswered words as blank entries
  const addUnansweredWords = (currentResults: ResultRow[]): ResultRow[] => {
    const unansweredWords: ResultRow[] = [];
    
    for (let i = 0; i < totalWords; i++) {
      const alreadyAttempted = currentResults.some(result => 
        words[i] && result.word === words[i].word
      );
      
      if (!alreadyAttempted) {
        unansweredWords.push({
          word: words[i].word,
          user_input: "", // Blank for unanswered
          type: words[i].word_type,
          time: 0,
          hints_used: 0,
        });
      }
    }
    
    return [...currentResults, ...unansweredWords];
  };

  // Helper function to find next unattempted question
  const findNextUnattemptedQuestion = (): number => {
    for (let i = currentIndex + 1; i < totalWords; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    // If no unattempted questions after current, check from beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    return -1; // No unattempted questions found
  };

  // Helper function to find next unattempted or skipped question (prioritizing skipped first)
  const findNextUnattemptedOrSkippedQuestion = (): number => {
    // First, look for skipped questions after current index
    for (let i = currentIndex + 1; i < totalWords; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then look for unattempted questions after current index
    for (let i = currentIndex + 1; i < totalWords; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    // If no skipped or unattempted questions after current, check from beginning for skipped
    for (let i = 0; i < currentIndex; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then check for unattempted questions from beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    return -1; // No unattempted or skipped questions found
  };

  const handleNextTyping = () => {
    const clean = userInput.trim().toLowerCase();
    if (!clean) return;
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    const isCorrect = clean === currentWord.word.toLowerCase();
    setLastResult(isCorrect ? 'correct' : 'incorrect');
    setTimeout(() => setLastResult(null), 900);

    // Check if this question was previously skipped
    const wasSkipped = skippedIndices.includes(currentIndex);
    let newResults;

    if (wasSkipped) {
      // Update existing entry instead of creating new one
      newResults = results.map(result => 
        result.word === currentWord.word 
          ? { ...result, user_input: clean, time: timeSpent }
          : result
      );
    } else {
      // Create new entry
      const row: ResultRow = {
        word: currentWord.word,
        user_input: clean,
        type: currentWord.word_type,
        time: timeSpent,
        hints_used: 0,
      };
      newResults = [...results, row];
    }

    setResults(newResults);
    setAttemptedIndices((prev) =>
      prev.includes(currentIndex) ? prev : [...prev, currentIndex]
    );
    // Remove from skipped list if it was previously skipped
    setSkippedIndices((prev) => prev.filter(index => index !== currentIndex));
    setIsComplete(true);

    // If this was the last question, end the test immediately
    if (currentIndex === totalWords - 1) {
      onComplete({ words: addUnansweredWords(newResults) });
      return;
    }

    // Otherwise, find next unattempted or skipped question (prioritizing skipped)
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      // If no more unattempted or skipped questions, complete the test
      onComplete({ words: addUnansweredWords(newResults) });
    }
  };

  const handleLeave = () => {
    // Add blank entries for all unanswered questions using helper function
    onComplete({ words: addUnansweredWords(results) });
  };

  const handleNextDrag = () => {
    const userWord = userPhonemes.map((x) => x || "").join("").trim().toLowerCase();
    if (!userWord) return;
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    const isCorrect = userWord === currentWord.word.toLowerCase();
    setLastResult(isCorrect ? 'correct' : 'incorrect');
    setTimeout(() => setLastResult(null), 900);

    // Check if this question was previously skipped
    const wasSkipped = skippedIndices.includes(currentIndex);
    let newResults;

    if (wasSkipped) {
      // Update existing entry instead of creating new one
      newResults = results.map(result => 
        result.word === currentWord.word 
          ? { ...result, user_input: userWord, time: timeSpent }
          : result
      );
    } else {
      // Create new entry
      const row: ResultRow = {
        word: currentWord.word,
        user_input: userWord,
        type: currentWord.word_type,
        time: timeSpent,
        hints_used: 0,
      };
      newResults = [...results, row];
    }

    setResults(newResults);
    setAttemptedIndices((prev) =>
      prev.includes(currentIndex) ? prev : [...prev, currentIndex]
    );
    // Remove from skipped list if it was previously skipped
    setSkippedIndices((prev) => prev.filter(index => index !== currentIndex));
    setIsComplete(true);

    // If this was the last question, end the test immediately
    if (currentIndex === totalWords - 1) {
      onComplete({ words: addUnansweredWords(newResults) });
      return;
    }

    // Otherwise, find next unattempted or skipped question (prioritizing skipped)
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      // If no more unattempted or skipped questions, complete the test
      onComplete({ words: addUnansweredWords(newResults) });
    }
  };

  const handleDropToSlot = (letter: string, index: number) => {
    setUserPhonemes((prev) => {
      const next = [...prev];
      next[index] = letter;
      return next;
    });
  };

  const handleReset = () => {
    if (isEffectiveDrag) setUserPhonemes(new Array(phoneme.length).fill(null));
    else setUserInput("");
    setIsComplete(false);
    // Reset timer when resetting the word
    setStartTime(Date.now());
    setCurrentWordTime(0);
  };

  const handleSkip = () => {
    // Check if this question was previously skipped
    const wasSkipped = skippedIndices.includes(currentIndex);
    let newResults;

    if (wasSkipped) {
      // If it was already skipped, don't create a new entry, just navigate
      newResults = results;
    } else {
      // Record a skipped row for new skips
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const row: ResultRow = {
        word: currentWord.word,
        user_input: "", // Explicitly blank for skipped
        type: currentWord.word_type,
        time: timeSpent,
        hints_used: 0,
      };
      newResults = [...results, row];
      setResults(newResults);
      setSkippedIndices((prev) => (prev.includes(currentIndex) ? prev : [...prev, currentIndex]));
    }
    
    setIsComplete(false);
    
    // Check if this is the last question
    if (currentIndex === totalWords - 1) {
      // If skipping the last question, end the test
      onComplete({ words: addUnansweredWords(newResults) });
      return;
    }
    
    // Find next unattempted or skipped question
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      // If no more unattempted or skipped questions, complete the test
      onComplete({ words: addUnansweredWords(newResults) });
    }
  };

  const audioRef = React.useRef<HTMLAudioElement>(null);
  const sentenceAudioRef = React.useRef<HTMLAudioElement>(null);
  const manualPlayRef = React.useRef(false);
  const cleanupAudioRef = React.useRef<null | (() => void)>(null);

  // Helper to get valid audio src
  const getAudioSrc = (audio: string) => {
    if (!audio) return "";
    if (audio.startsWith("data:audio")) return audio;
    return `data:audio/mp3;base64,${audio}`;
  };

  // Play word audio, then sentence audio automatically
  useEffect(() => {
    // Reset manual play flag when new word loads
    manualPlayRef.current = false;

    if (currentWord.word_audio && audioRef.current) {
      // Add a short delay before announcing the new word so the transition
      // from clicking "Next" doesn't feel too abrupt.
      const startDelay = 600;
      let didPlay = false;

      const startTimer = setTimeout(() => {
        // If the user already started a manual play during the delay, skip auto-play
        if (manualPlayRef.current) return;
        didPlay = true;
        // Create a new audio instance for this word to avoid stale references
        const newAudio = new Audio(getAudioSrc(currentWord.word_audio));
        newAudio.style.display = 'none';
        newAudio.currentTime = 0;
        newAudio.playbackRate = 1;

        // Update the ref to the new audio
        audioRef.current = newAudio;

        newAudio.play().catch(() => {});

        // When word audio ends, wait for a gap then play sentence audio slowly
        const handleWordEnded = () => {
          // if the user manually clicked Play Word, do not auto-play sentence
          if (manualPlayRef.current) {
            return;
          }
          if (currentWord.sentence_audio && sentenceAudioRef.current) {
            // Add a 1 second gap before playing sentence
            setTimeout(() => {
              if (sentenceAudioRef.current) {
                sentenceAudioRef.current.currentTime = 0;
                sentenceAudioRef.current.playbackRate = 0.85; // Slower playback for auto-play
                sentenceAudioRef.current.play().catch(() => {});
              }
            }, 1000);
          }
        };

        // Add event listener to the new audio element
        newAudio.addEventListener('ended', handleWordEnded);

        // Store cleanup for the audio listener
        cleanupAudioRef.current = () => {
          if (newAudio) {
            newAudio.removeEventListener('ended', handleWordEnded);
            newAudio.pause();
          }
        };
      }, startDelay);

      return () => {
        clearTimeout(startTimer);
        if (didPlay && cleanupAudioRef.current) {
          cleanupAudioRef.current();
          cleanupAudioRef.current = null;
        }
      };
    }
  }, [currentWord.word_audio, currentWord.sentence_audio]);

  const handlePlayWord = (rate = 1) => {
    // Mark as manual play BEFORE doing anything else
    manualPlayRef.current = true;
    
    if (audioRef.current) {
      // Stop any currently playing sentence audio first
      if (sentenceAudioRef.current) {
        sentenceAudioRef.current.pause();
        sentenceAudioRef.current.currentTime = 0;
      }
      
      try {
        // Simply reuse the existing audio element instead of DOM manipulation
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.playbackRate = rate;
        
        // Remove any existing event listeners by cloning the node (without DOM replacement)
        const currentAudio = audioRef.current;
        const newAudio = currentAudio.cloneNode(false) as HTMLAudioElement;
        newAudio.src = getAudioSrc(currentWord.word_audio);
        newAudio.playbackRate = rate;
        newAudio.style.display = 'none';
        
        // Update the ref to the new audio element (no DOM manipulation)
        audioRef.current = newAudio;
        
        // Play the audio
        newAudio.play().catch(() => {});
      } catch (e) {
        // Ultimate fallback - just use the current audio element as-is
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.playbackRate = rate;
          audioRef.current.play().catch(() => {});
        } catch (fallbackError) {
          // ignore
        }
      }
    }
  };

  const handlePlaySentence = () => {
    if (sentenceAudioRef.current) {
      // Stop any currently playing word audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      try {
        sentenceAudioRef.current.pause();
        sentenceAudioRef.current.currentTime = 0;
        sentenceAudioRef.current.playbackRate = 1;
        sentenceAudioRef.current.play().catch(() => {});
      } catch (e) {}
    }
  };

  const handlePlaySentenceSlow = (rate = 0.8) => {
    if (sentenceAudioRef.current) {
      // Stop any currently playing word audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      try {
        sentenceAudioRef.current.pause();
        sentenceAudioRef.current.currentTime = 0;
        sentenceAudioRef.current.playbackRate = rate;
        sentenceAudioRef.current.play().catch(() => {});
      } catch (e) {}
    }
  };

  // Multi-backend configuration for both mouse and touch support
  const HTML5toTouch = {
    backends: [
      {
        id: 'html5',
        backend: HTML5Backend,
        transition: MouseTransition,
      },
      {
        id: 'touch',
        backend: TouchBackend,
        options: { enableMouseEvents: true },
        preview: true,
        transition: TouchTransition,
      },
    ],
  };

  /* ----------------------------- JSX ----------------------------- */
  return (
    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
    <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex flex-col font-[Nunito_Sans,sans-serif]">
  <div className="three-col-layout flex flex-1 gap-4 lg:gap-6 items-start unified-medium-scale">
          {/* Desktop: left question navigation (restored) */}
          <div className="left-col hidden lg:flex lg:flex-col lg:w-96 xl:w-[420px] p-4 flex-shrink-0 unified-medium-panel">
            <div className="sticky top-20">
              <div className="w-full p-4 rounded-2xl bg-gradient-to-b from-[#F3E8FF] to-white/80 shadow-md border border-gray-100 lg:origin-top-left unified-medium-inner">
                <QuestionPalette
                  total={totalWords}
                  currentIndex={currentIndex}
                  attempted={attemptedIndices}
                  skipped={skippedIndices}
                  onJump={(idx) => { 
                    // Only allow navigation to skipped questions or current question
                    if (skippedIndices.includes(idx) || idx === currentIndex) {
                      setCurrentIndex(idx); 
                      window.scrollTo({ top: 0, behavior: 'smooth' }); 
                    }
                  }}
                  isMobile={false}
                  isTablet={false}
                  isDesktopLeft={true}
                  collapsed={false}
                />
              </div>
            </div>
          </div>

          {/* Center Main Card (left column) */}
          <div className="center-col flex-1 flex flex-col items-center lg:origin-top-left min-w-0 unified-medium-panel">
            <div className="center-inner w-full max-w-2xl mx-auto mt-0 md:mt-4 p-2 md:p-4 unified-medium-inner flex flex-col flex-1">
              <div className="flex items-center justify-between mb-4">
                <Button
                  onClick={onBackToDashboard}
                  variant="outline"
                  size="sm"
                  className="bg-white/80 hover:bg-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <TestBadge test="spelling" subject={playerName} />
                    {isMobile &&<div className="text-xs text-gray-600">Question {currentIndex + 1} of {totalWords}</div>}
                  </div>
                  
                  {/* Mobile Menu Button */}
                  <div className="relative lg:hidden">
                    <Button
                      onClick={() => setShowMobileMenu(!showMobileMenu)}
                      variant="outline"
                      size="sm"
                      className="bg-white/80 hover:bg-white p-2"
                    >
                      <Menu className="w-4 h-4" />
                    </Button>
                    
                    {/* Mobile Side Navigation */}
                    {showMobileMenu && (
                      <>
                        {/* Backdrop */}
                        <div 
                          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                          onClick={() => setShowMobileMenu(false)}
                        />
                        
                        {/* Side Navigation Panel */}
                        <div className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col ${
                          showMobileMenu ? 'translate-x-0' : 'translate-x-full'
                        }`}>
                          {/* Header */}
                          <div className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] p-4 text-white flex-shrink-0">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-bold">Question Navigation</h3>
                              <button
                                onClick={() => setShowMobileMenu(false)}
                                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="mt-2 text-sm opacity-90">
                              Question {currentIndex + 1} of {totalWords}
                            </div>
                          </div>
                          
                          {/* End Test Button - Top Priority */}
                          <div className="p-4 border-b border-gray-100">
                            <button 
                              onClick={() => {
                                handleLeave();
                                setShowMobileMenu(false);
                              }} 
                              className="w-full py-3 rounded-xl text-white font-bold shadow-md bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <span>🏁</span>
                              <span>End Test</span>
                            </button>
                          </div>

                          {/* Scrollable Content */}
                          <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                            <div className="bg-gradient-to-b from-[#F3E8FF] to-white/80 rounded-xl p-4 shadow-sm">
                              <QuestionPalette
                                total={totalWords}
                                currentIndex={currentIndex}
                                attempted={attemptedIndices}
                                skipped={skippedIndices}
                                onJump={(idx) => { 
                                  // Only allow navigation to skipped questions or current question
                                  if (skippedIndices.includes(idx) || idx === currentIndex) {
                                    setCurrentIndex(idx); 
                                    setShowMobileMenu(false);
                                    window.scrollTo({ top: 0, behavior: 'smooth' }); 
                                  }
                                }}
                                isMobile={true}
                                isTablet={false}
                                isDesktopLeft={false}
                                collapsed={false}
                              />
                            </div>
                            
                            {/* Additional Info Section */}
                            <div className="mt-6 space-y-4">
                              <div className="bg-blue-50 rounded-xl p-4">
                                <h4 className="font-semibold text-[#55608A] mb-2">Test Progress</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Completed:</span>
                                    <span className="font-medium text-blue-600">{attemptedIndices.length} / {totalWords}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Current Time:</span>
                                    <span className="font-medium text-blue-600">{currentWordTime}s</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Grade Level:</span>
                                    <span className="font-medium text-blue-600">{gradeName}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <Card className="p-6 rounded-3xl shadow-xl bg-white/95 flex flex-col items-center pb-32 sm:pb-8 h-full">
                {/* Result badge overlay */}
                {/* {lastResult && (
                  <div className={`result-badge absolute top-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-bold ${lastResult === 'correct' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                    {lastResult === 'correct' ? 'Correct ✅' : 'Try again ✖️'}
                  </div>
                )} */}
                {/* Prominent progress bar + rotating motivational message */}
                <div className="w-full mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-[#6B6FA3]">Question {currentIndex + 1} of {totalWords}</div>
                    <div className="flex items-center gap-4">
                      <div className="text-sm font-semibold text-[#55608A]">{currentMotivation}</div>
                      <div className="text-sm font-bold text-[#8E7BFF] bg-white/80 px-3 py-1 rounded-full border border-[#EDE9FF]">
                        Time: {currentWordTime}s
                      </div>
                    </div>
                  </div>
                  {/* More visible pastel gradient progress track */}
                  <div
                    className="w-full h-4 rounded-full overflow-hidden shadow-sm relative"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progressPercentage)}
                    aria-label={`Progress: ${Math.round(progressPercentage)}%`}
                  >
                    {/* Track (soft pastel background) */}
                    <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#F3E8FF] via-[#FFF7EE] to-[#F8FBFF] opacity-95 z-0" />
                    {/* Fill (stronger peach→coral) */}
                    <div
                      style={{ width: `${progressPercentage}%` }}
                      className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] shadow-md transition-all z-10"
                    />
                  </div>
                </div>
                {/* Hidden audio elements for word and sentence playback */}
                <audio
                  ref={audioRef}
                  style={{ display: 'none' }}
                  src={getAudioSrc(currentWord.word_audio)}
                  key={currentWord.word}
                />
                <audio
                  ref={sentenceAudioRef}
                  style={{ display: 'none' }}
                  src={getAudioSrc(currentWord.sentence_audio)}
                  key={currentWord.word + '-sentence'}
                />
                <div className="mb-4">
                  <div className="flex items-center justify-center">
                    <div className="w-[10rem] h-[10rem] rounded-full overflow-hidden shadow-md border-2 border-gray-200">
                      <img src={login_hero_image} alt="Eko Icon" className="w-full h-full object-cover" />
                    </div>
                  </div>
                  {/* audio controls (integrated near center) */}
                  <div className="mt-4 flex items-center gap-3 justify-center">
                    <button onClick={() => handlePlayWord(1)} className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white font-semibold shadow-lg hover:brightness-95 transition">
                      <Play className="w-4 h-4" />
                      <span className="text-sm">Play word</span>
                    </button>
                    {currentWord.sentence_audio && (
                      <button onClick={handlePlaySentence} className="flex items-center gap-2 px-4 py-3 rounded-full bg-[#F5F7FF] text-[#6B6FA3] font-semibold border border-[#EDE9FF] shadow-sm hover:brightness-95 transition">
                        <Volume2 className="w-4 h-4" />
                        <span className="text-sm">Play sentence</span>
                      </button>
                    )}
                  </div>
                </div>
                <div className="mb-4 text-center">
                  {isEffectiveDrag ? (
                    <>
                      <div className="text-xl font-bold text-[#8E7BFF] mb-1">Drag and drop the letters to spell the word!</div>
                      <div className="text-base text-gray-600">Drag the letters below into the boxes:</div>
                      {gradeNameState === "Kindergarten" && firstName && (
                        <div className="text-sm text-gray-500 italic mt-1">
                          Parents: feel free to help {firstName} type or drag the letters if needed
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xl font-bold text-[#8E7BFF] mb-1">Now type what you heard!</div>
                  )}
                </div>
                {/* Toggle button: switch between drag-and-drop and typing (Kindergarten only) */}
                {gradeNameState === "Kindergarten" && (
                  <div className="flex justify-center mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setUseTypingMode((prev) => !prev);
                        // Clear any in-progress answer when switching modes
                        setUserInput("");
                        setUserPhonemes(new Array(phoneme.length).fill(null));
                        setIsComplete(false);
                      }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 border-[#EDE9FF] bg-white text-[#6B6FA3] shadow-sm hover:brightness-95 transition-all"
                    >
                      {useTypingMode ? "🔀 Switch to Drag & Drop" : "⌨️ Switch to Typing"}
                    </button>
                  </div>
                )}
                {/* Kindergarten: Drag-and-drop interface (or typing when toggled) */}
                {isEffectiveDrag ? (
                  <>
                    <div className="flex justify-center gap-2 mb-6">
                      {userPhonemes.map((letter, idx) => (
                        <DropZone key={idx} index={idx} letter={letter} onDrop={handleDropToSlot} />
                      ))}
                    </div>
                    <div className="text-center mb-4 block sm:hidden">
                      <p className="text-sm text-gray-600 font-medium bg-blue-50 p-2 rounded-lg">
                        📱 Touch and drag letters to spell the word
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 mb-4 p-2">
                      {letterPool.map((letter, idx) => (
                        <DraggableLetter key={idx} letter={letter} />
                      ))}
                    </div>
                  </>
                ) : (
                  <Input
                    ref={typingInputRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && isTypingComplete && handleNextTyping()
                    }
                    placeholder="Type the word you heard..."
                    className="text-center text-lg p-3 h-12 border-2 border-[#EDE9FF] rounded-2xl w-full mb-4"
                    disabled={isComplete}
                    autoFocus
                  />
                )}
                
                {/* Grade is selected on the Dashboard — no dropdown here */}
                <div className="flex gap-3 justify-center w-full mb-4 flex-wrap">
                  <Button
                    onClick={isEffectiveDrag ? handleNextDrag : handleNextTyping}
                    disabled={isComplete || !isAnswerComplete}
                    className={`min-w-[8rem] h-12 rounded-full text-lg font-bold shadow-lg transition-all ${
                      isComplete || !isAnswerComplete
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95'
                    }`}
                  >
                    {currentIndex === totalWords - 1 ? 'Submit →' : 'Next →'}
                  </Button>
                  <Button
                    onClick={handleSkip}
                    size="lg"
                    variant="outline"
                    className="min-w-[6.5rem] h-12 rounded-full text-lg font-semibold border-2 border-[#EDE9FF] text-[#6B6FA3] bg-white"
                  >
                    Skip
                  </Button>
                </div>
                
                {/* Helpful message when Next is disabled */}
                {!isAnswerComplete && !isComplete && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-[#6B6FA3] bg-[#F6F6FF] border border-[#EDE9FF] rounded-xl px-4 py-2 inline-block">
                      {isEffectiveDrag
                        ? "💡 Drag all letters to spell the word to continue"
                        : "💡 Type your answer to continue"
                      }
                    </p>
                  </div>
                )}
                
                {/* Progress bar is shown at the top with motivational text; central duplicate audio controls removed */}
              </Card>
            </div>
{/* Question navigation now accessible through mobile side nav */}
          </div>

          {/* Right Sidebar: Audio Controls (desktop only) */}
          <div className="right-col hidden lg:flex flex-col  p-6 lg:p-8 bg-gradient-to-b from-blue-50 via-white to-blue-100 rounded-xl shadow-lg min-h-[500px] max-h-full overflow-auto border border-blue-200 lg:w-96 xl:w-[520px] lg:origin-top-right flex-shrink-0 unified-medium-panel" style={{ marginTop: '15px' }}>
            <div className="unified-medium-inner">
            {/* add a small top margin on desktop so the panel doesn't stick to the very top */}
            <div className="mt-6 lg:mt-0 sticky top-20 w-full">
              <div className="w-full flex flex-col gap-4 lg:gap-6">
                <h2 className="font-bold text-xl lg:text-2xl mb-4 text-[#55608A]">Audio Controls</h2>
                <div className="space-y-3 w-full">
                  <button 
                    className="w-full py-4 rounded-2xl text-[#4C4770] text-base lg:text-lg font-semibold shadow-md border border-[#E8E8FF] bg-gradient-to-r from-[#F3E8FF] to-[#F8F0FF] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2" 
                    onClick={() => handlePlayWord(0.8)}
                  >
  
                    <span>Play Word (Slow)</span>
                  </button>
                  <button 
                    className="w-full py-4 rounded-2xl text-[#4C4770] text-base lg:text-lg font-semibold shadow-md border border-[#E8E8FF] bg-gradient-to-r from-[#F3E8FF] to-[#F8F0FF] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2" 
                    onClick={() => handlePlayWord(0.6)}
                  >
                
                    <span>Play Word (Slower)</span>
                  </button>
                  <button 
                    className="w-full py-4 rounded-2xl text-[#154ECA] text-base lg:text-lg font-semibold shadow-md border border-[#E6F0FF] bg-gradient-to-r from-[#F0FAFF] to-[#E6F4FF] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2" 
                    onClick={() => handlePlaySentenceSlow(0.85)}
                    style={{ display: currentWord.sentence_audio ? 'flex' : 'none' }}
                  >
                   
                    <span>Play Sentence (Slow)</span>
                  </button>
                </div>
                  <div className="space-y-3 text-base lg:text-lg mt-2 w-full">
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Audio Status</span><br />
                    <span className="text-green-600 font-bold lg:text-lg">Ready</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Current Question</span><br />
                    <span className="text-blue-600 font-bold lg:text-lg">{currentIndex + 1} of {totalWords}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Grade Level</span><br />
                    <span className="text-orange-600 font-bold lg:text-lg">{gradeName}</span>
                  </div>
                </div>
                <button onClick={handleLeave} className="w-full mb-2 py-3 lg:py-5 rounded-lg text-white text-base lg:text-xl lg:font-extrabold shadow transition" style={{ background: 'linear-gradient(90deg,#F7B26A,#ECA7C1)' }}>End Test</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer style={{ marginTop: 10, color: '#6B6FA3', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
  </div>
  </DndProvider>
  );
}
